import { useCallback } from "react";
import { plan_bottom_up_periods_path } from "@/lib/routes";
import { SAVE_ERROR_MESSAGE, type CellRef } from "./useSaveStatus";
import type { Row } from "../byMonthTypes";

interface Args {
  marketSlug: string;
  planSlug: string;
  channelId: string;
  setLocalRows: React.Dispatch<React.SetStateAction<Row[]>>;
  markSaved: (cells: CellRef[]) => void;
  markError: (cells: CellRef[], message?: string) => void;
}

// Month-cell write handlers for the by-month edit page: optimistic local
// update + PATCH to Plan::BottomUpController#upsert_period.
export function useByMonthCellWrites({
  marketSlug,
  planSlug,
  channelId,
  setLocalRows,
  markSaved,
  markError,
}: Args) {
  const handlePasteCells = useCallback(
    (productExternalId: string, updates: { period_date: string; quantity: number }[]) => {
      if (updates.length === 0) return;

      const cells = updates.map((u) => ({ productExternalId, periodDate: u.period_date }));
      const byDate = new Map(updates.map((u) => [u.period_date, u.quantity]));
      setLocalRows((prev) =>
        prev.map((r) =>
          r.product_external_id !== productExternalId
            ? r
            : {
                ...r,
                months: r.months.map((m) =>
                  byDate.has(m.period_date)
                    ? { ...m, quantity: byDate.get(m.period_date) as number, filled: true }
                    : m,
                ),
              },
        ),
      );

      const csrfToken =
        document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";
      fetch(plan_bottom_up_periods_path(marketSlug, planSlug), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          cells: updates.map((u) => ({
            product_external_id: productExternalId,
            granularity: "month",
            period_date: u.period_date,
            quantity: u.quantity,
            org_node_id: channelId,
          })),
        }),
      })
        .then((res) => {
          if (res.ok) return markSaved(cells);
          // The server explains a refusal (403/422) in the JSON body; show that.
          return res
            .json()
            .then((body) => markError(cells, body?.error || SAVE_ERROR_MESSAGE))
            .catch(() => markError(cells, SAVE_ERROR_MESSAGE));
        })
        .catch(() => markError(cells, SAVE_ERROR_MESSAGE));
    },
    [marketSlug, planSlug, channelId, setLocalRows, markSaved, markError],
  );

  const handleCellChange = useCallback(
    (productExternalId: string, periodDate: string, quantity: number) =>
      handlePasteCells(productExternalId, [{ period_date: periodDate, quantity }]),
    [handlePasteCells],
  );

  return { handleCellChange, handlePasteCells };
}
